import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import ArticleShare from "@/components/ArticleShare";
import { getTrendsPost, trendsPosts } from "@/data/trends";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import {
  Globe,
  TrendingUp,
  MapPin,
  Sparkles,
  Leaf,
  Palette,
  Layers,
  ShieldCheck,
  Compass,
  Building2,
  BookOpen,
} from "lucide-react";

const SECTION_ICONS = {
  globe: Globe,
  "trending-up": TrendingUp,
  "map-pin": MapPin,
  sparkles: Sparkles,
  leaf: Leaf,
  palette: Palette,
  layers: Layers,
  "shield-check": ShieldCheck,
  compass: Compass,
  building: Building2,
  default: BookOpen,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);

export const TrendsPost = () => {
  const { id } = useParams();
  const data = getTrendsPost(id);

  // Build TOC from H2 blocks (auto-anchor ids).
  const toc = useMemo(() => {
    if (!data) return [];
    return data.body
      .map((b, i) => (b.type === "h2" ? { text: b.text, icon: b.icon, anchor: `s-${i}-${slugify(b.text)}` } : null))
      .filter(Boolean);
  }, [data]);

  // Chronological prev/next based on datePublished.
  const { prev, next } = useMemo(() => {
    if (!data) return { prev: null, next: null };
    const sorted = [...trendsPosts].sort(
      (a, b) => new Date(a.datePublished) - new Date(b.datePublished)
    );
    const idx = sorted.findIndex((p) => p.id === data.id);
    return {
      prev: idx > 0 ? sorted[idx - 1] : null,
      next: idx < sorted.length - 1 ? sorted[idx + 1] : null,
    };
  }, [data]);

  // Extract FAQ Q/A pairs from the body for FAQPage JSON-LD. Guides use
  // an H2 titled "Frequently asked …" followed by alternating H3 (question)
  // + P (answer). We only emit FAQPage when at least 3 pairs are found so
  // schema always matches visible content exactly.
  const faqPairs = useMemo(() => {
    if (!data) return [];
    const pairs = [];
    let inFaq = false;
    let pendingQ = null;
    for (const b of data.body) {
      if (b.type === "h2" && b.text && /frequently asked/i.test(b.text)) {
        inFaq = true;
        pendingQ = null;
        continue;
      }
      if (b.type === "h2" && inFaq) {
        // A new top-level section ends the FAQ block.
        inFaq = false;
        pendingQ = null;
        continue;
      }
      if (!inFaq) continue;
      if (b.type === "h3" && b.text) pendingQ = b.text;
      else if (b.type === "p" && b.text && pendingQ) {
        pairs.push([pendingQ, b.text]);
        pendingQ = null;
      }
    }
    return pairs;
  }, [data]);

  useSeo({
    title: data ? (data.metaTitle || `${data.title} | Asia Sourcing India`) : "Trends & News",
    description: data?.metaDescription,
    path: data ? `/trends/${data.id}` : "/trends",
    canonical: data?.canonical,
    image: data?.image,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${SITE_URL}/trends/${data.id}#article`,
              headline: data.title,
              description: data.metaDescription,
              image: data.images?.length
                ? data.images.map((im) => ({
                    "@type": "ImageObject",
                    url: `${SITE_URL}${im.src}`,
                    width: im.width,
                    height: im.height,
                    caption: im.alt,
                  }))
                : data.image,
              datePublished: data.datePublished,
              dateModified: data.dateModified,
              inLanguage: "en-IN",
              author: {
                "@type": "Organization",
                name: "Asia Sourcing India",
                url: SITE_URL,
              },
              publisher: { "@id": `${SITE_URL}#organization` },
              mainEntityOfPage: data.canonical || `${SITE_URL}/trends/${data.id}`,
              articleSection: data.categoryLabel,
            },
            breadcrumb([
              { name: "Home", item: `${SITE_URL}/` },
              { name: "Insights", item: `${SITE_URL}/trends` },
              { name: data.title, item: `${SITE_URL}/trends/${data.id}` },
            ]),
            {
              "@type": ["Organization", "LocalBusiness"],
              "@id": `${SITE_URL}#organization`,
              name: "Asia Sourcing India",
              url: SITE_URL,
              logo: `${SITE_URL}/assets/og.jpg`,
            },
            ...(faqPairs.length >= 3
              ? [
                  {
                    "@type": "FAQPage",
                    "@id": `${SITE_URL}/trends/${data.id}#faq`,
                    mainEntity: faqPairs.map(([q, a]) => ({
                      "@type": "Question",
                      name: q,
                      acceptedAnswer: { "@type": "Answer", text: a },
                    })),
                  },
                ]
              : []),
          ],
        }
      : undefined,
  });

  const [activeAnchor, setActiveAnchor] = useState("");
  useEffect(() => {
    if (!data) return;
    const onScroll = () => {
      let best = "";
      for (const t of toc) {
        const el = document.getElementById(t.anchor);
        if (el && el.getBoundingClientRect().top < 120) best = t.anchor;
      }
      setActiveAnchor(best);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [data, toc]);

  if (!data) return <Navigate to="/trends" replace />;

  const currentUrl = data.canonical || `${SITE_URL}/trends/${data.id}`;

  return (
    <article data-testid="trends-post">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem", maxWidth: "1080px" }}
      >
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p className="mono" style={{ fontSize: ".72rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>
            <Link to="/" style={{ color: "inherit" }}>Home</Link> ·{" "}
            <Link to="/trends" style={{ color: "inherit" }}>Insights</Link> ·{" "}
            {data.categoryLabel}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="trends-post-eyebrow">
            {data.categoryLabel} · {data.readTime} · {formatDate(data.datePublished)}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={data.title}
          className="display tp-title"
          stagger={40}
          testId="trends-post-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.15rem",
            marginTop: "1.6rem",
            lineHeight: 1.65,
            fontFamily: "var(--display)",
            fontWeight: 380,
            letterSpacing: "-.005em",
            maxWidth: "72ch",
          }}
        >
          {data.excerpt}
        </Reveal>
      </header>

      {/* HERO IMAGE */}
      {data.image && (
        <div
          className="wrap"
          style={{ paddingBlock: "1rem var(--section)", maxWidth: "1200px" }}
          data-testid="trends-post-hero-image"
        >
          <Reveal>
            <img
              src={data.image}
              alt={data.imageAlt || data.title}
              width="1600"
              height="900"
              loading="eager"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                maxHeight: "560px",
                objectFit: "cover",
                border: "1px solid var(--line)",
              }}
            />
          </Reveal>
        </div>
      )}

      {/* SHARE + TOC + BODY */}
      <div
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          maxWidth: "1200px",
          borderTop: "1px solid var(--line)",
          display: "grid",
          gridTemplateColumns: "minmax(0, 260px) minmax(0, 1fr)",
          gap: "clamp(2rem, 4vw, 3.6rem)",
          alignItems: "start",
        }}
      >
        {/* ── LEFT SIDEBAR — TOC + Share (sticky) ── */}
        <aside
          className="trends-post-aside"
          style={{ position: "sticky", top: "100px" }}
          data-testid="trends-post-toc-aside"
        >
          {toc.length > 0 && (
            <nav aria-label="Table of contents" data-testid="trends-post-toc">
              <p className="mono" style={{ fontSize: ".68rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase", margin: "0 0 1rem" }}>
                In this article
              </p>
              <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: ".55rem" }}>
                {toc.map((t, i) => (
                  <li key={t.anchor}>
                    <a
                      href={`#${t.anchor}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(t.anchor);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      data-testid={`trends-post-toc-${i}`}
                      style={{
                        display: "flex",
                        gap: ".55rem",
                        alignItems: "flex-start",
                        textDecoration: "none",
                        color: activeAnchor === t.anchor ? "var(--brass-deep)" : "var(--ink-soft)",
                        fontSize: ".9rem",
                        lineHeight: 1.4,
                        borderLeft: activeAnchor === t.anchor ? "2px solid var(--brass-deep)" : "2px solid transparent",
                        paddingLeft: ".7rem",
                        transition: "color .3s, border-color .3s",
                      }}
                    >
                      <span style={{ fontFamily: "var(--mono)", fontSize: ".68rem", opacity: .7, marginTop: ".18rem" }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{t.text}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}
          <div style={{ marginTop: "2rem", paddingTop: "1.4rem", borderTop: "1px solid var(--line)" }}>
            <ArticleShare url={currentUrl} title={data.title} />
          </div>
        </aside>

        {/* ── ARTICLE BODY ── */}
        <section data-testid="trends-post-body" style={{ minWidth: 0 }}>
          {data.body.map((block, i) => {
            if (block.type === "h2") {
              const Icon = SECTION_ICONS[block.icon] || SECTION_ICONS.default;
              return (
                <Reveal
                  key={i}
                  as="h2"
                  id={`s-${i}-${slugify(block.text)}`}
                  className="display"
                  style={{
                    fontSize: "clamp(1.5rem, 2.6vw, 2.15rem)",
                    margin: "2.6rem 0 1.2rem",
                    letterSpacing: "-.01em",
                    lineHeight: 1.15,
                    maxWidth: "68ch",
                    display: "flex",
                    gap: ".8rem",
                    alignItems: "center",
                    scrollMarginTop: "100px",
                  }}
                >
                  <Icon
                    size={26}
                    color="var(--brass-deep)"
                    strokeWidth={1.4}
                    aria-hidden="true"
                    style={{ flexShrink: 0 }}
                  />
                  <span>{block.text}</span>
                </Reveal>
              );
            }
            if (block.type === "h3") {
              return (
                <Reveal
                  key={i}
                  as="h3"
                  className="display"
                  style={{
                    fontSize: "clamp(1.15rem, 1.9vw, 1.4rem)",
                    margin: "1.8rem 0 .8rem",
                    letterSpacing: "-.005em",
                    lineHeight: 1.25,
                    color: "var(--ink)",
                    fontWeight: 500,
                  }}
                >
                  {block.text}
                </Reveal>
              );
            }
            if (block.type === "ul") {
              return (
                <Reveal
                  key={i}
                  as="ul"
                  className="cap-list"
                  style={{ margin: "0 0 1.4rem", maxWidth: "70ch" }}
                >
                  {(block.items || []).map((it, k) => (
                    <li key={k}>
                      <span>{it}</span>
                    </li>
                  ))}
                </Reveal>
              );
            }
            if (block.type === "img") {
              // Editorial image block — WebP with JPG fallback, fixed intrinsic
              // dimensions (no CLS), lazy below the fold, eager+high priority
              // when flagged as the article hero.
              const eager = !!block.eager;
              return (
                <Reveal
                  key={i}
                  as="figure"
                  className="article-figure"
                  style={{
                    margin: "2.2rem 0 2.4rem",
                    maxWidth: block.full ? "none" : "72ch",
                  }}
                >
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={`${block.src.replace(/\.jpg$/, ".webp")}`}
                    />
                    <img
                      src={block.src}
                      alt={block.alt}
                      width={block.width}
                      height={block.height}
                      loading={eager ? "eager" : "lazy"}
                      fetchpriority={eager ? "high" : "auto"}
                      decoding="async"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 6,
                        border: "1px solid var(--line)",
                      }}
                    />
                  </picture>
                  {block.caption && (
                    <figcaption
                      className="mono"
                      style={{
                        marginTop: ".6rem",
                        fontSize: ".68rem",
                        letterSpacing: ".08em",
                        color: "var(--ink-soft)",
                        textTransform: "uppercase",
                      }}
                    >
                      {block.caption}
                    </figcaption>
                  )}
                </Reveal>
              );
            }
            return (
              <Reveal
                key={i}
                as="p"
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.05rem",
                  lineHeight: 1.8,
                  margin: "0 0 1.2rem",
                  maxWidth: "68ch",
                }}
              >
                {block.text}
              </Reveal>
            );
          })}
        </section>
      </div>

      {/* ── PREV / NEXT ── */}
      {(prev || next) && (
        <section
          className="wrap"
          style={{
            paddingBlock: "var(--section)",
            borderTop: "1px solid var(--line)",
            maxWidth: "1200px",
          }}
          data-testid="trends-post-nav"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1.4rem",
            }}
          >
            {prev ? (
              <Link
                to={`/trends/${prev.id}`}
                data-testid="trends-post-prev"
                style={{
                  display: "block",
                  padding: "1.4rem 1.4rem",
                  border: "1px solid var(--line)",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  ← Previous article
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: ".5rem 0 0", lineHeight: 1.3 }}>
                  {prev.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/trends/${next.id}`}
                data-testid="trends-post-next"
                style={{
                  display: "block",
                  padding: "1.4rem 1.4rem",
                  border: "1px solid var(--line)",
                  textDecoration: "none",
                  color: "inherit",
                  textAlign: "right",
                }}
              >
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  Next article →
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: ".5rem 0 0", lineHeight: 1.3 }}>
                  {next.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </section>
      )}

      {/* ── CTA — Looking to Source from India? ── */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="trends-post-source-cta"
      >
        <Reveal as="p" className="eyebrow">Ready to source?</Reveal>
        <Reveal as="h2" className="display">
          Looking to Source from India?
        </Reveal>
        <Reveal
          as="p"
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            lineHeight: 1.65,
            marginTop: "1.4rem",
            maxWidth: "62ch",
          }}
        >
          Whether you&apos;re developing a new collection or looking for reliable manufacturing partners, our sourcing experts are ready to help.
        </Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="trends-post-cta-contact">
            Contact Our Team →
          </Link>
          <Link to="/trends" className="btn btn-lg" data-testid="trends-post-cta-back">
            Back to Insights
          </Link>
        </Reveal>
      </section>

      {/* ── NEWSLETTER SIGNUP ── */}
      <NewsletterSubscribe />
    </article>
  );
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default TrendsPost;
